// src/pages/groups/StudentProgressDrawer.jsx
// التقدم التفصيلي للطالب (المستويات + كل البرامج + المهارات)
import { useEffect, useState } from "react";
import { groupService, getErrorMessage } from "../../services/groupService";
import { Avatar, Drawer, EmptyState, ProgressBar, Spinner, fmt, formatDate, formatRelative } from "./groupsShared";

const SKILL_LABELS = {
  reading: "القراءة",
  writing: "الكتابة",
  listening: "الاستماع",
  speaking: "التحدث",
  grammar: "القواعد",
  vocabulary: "المفردات",
};

function ProgramCard({ program, open, onToggle }) {
  return (
    <div className="gp-prog-card">
      <button type="button" onClick={onToggle} aria-expanded={open}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontWeight: 600, fontSize: 13.5 }}>
          <span>{program.program_label}</span>
          <span className="gp-num">{fmt(program.total_score)} نقطة</span>
        </div>
        <div className="gp-prog-meta">
          <span>
            شاهد {fmt(program.viewed_questions)} من {fmt(program.total_questions)} سؤال
          </span>
          <span>{Math.round(program.progress_percent)}%</span>
        </div>
        <ProgressBar value={program.progress_percent} />
        <div className="gp-muted" style={{ marginTop: 8 }}>
          آخر نشاط: {formatRelative(program.last_activity)}
        </div>
      </button>

      {open &&
        (program.skills?.length ? (
          program.skills.map((s) => (
            <div className="gp-skill-row" key={s.skill_id}>
              <div className="top">
                <span>
                  <b style={{ fontWeight: 600 }}>{s.title}</b>
                  {s.context && <span className="gp-muted" style={{ display: "block" }}>{s.context}</span>}
                </span>
                <span className="gp-num">{fmt(s.total_score)}</span>
              </div>
              <ProgressBar value={s.progress_percent} />
              <div className="gp-muted" style={{ marginTop: 5 }}>
                {fmt(s.viewed_questions)} / {fmt(s.total_questions)} سؤال
              </div>
            </div>
          ))
        ) : (
          <div className="gp-skill-row gp-muted">لا توجد مهارات مبدوءة في هذا البرنامج.</div>
        ))}
    </div>
  );
}

export default function StudentProgressDrawer({ groupId, studentId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [openPrograms, setOpenPrograms] = useState({});

  useEffect(() => {
    let off = false;
    setData(null);
    setError("");
    groupService
      .getStudentProgress(groupId, studentId)
      .then((res) => !off && setData(res.data))
      .catch((e) => !off && setError(getErrorMessage(e)));
    return () => {
      off = true;
    };
  }, [groupId, studentId]);

  const p = data?.progress;
  const programs = p?.programs || [];

  return (
    <Drawer title="تقدم الطالب" onClose={onClose}>
      {error && <div className="gp-error">{error}</div>}
      {!data && !error && <Spinner />}

      {data && (
        <>
          <div className="gp-who" style={{ marginBottom: 18 }}>
            <Avatar name={data.full_name} src={data.profile_picture_url} size={48} />
            <div style={{ minWidth: 0 }}>
              <b style={{ fontSize: 15 }}>{data.full_name}</b>
              <small>{data.email}</small>
            </div>
          </div>

          <div className="gp-levels">
            <div className="gp-level">
              المستوى العام
              <b>{p?.overall_level_label || "—"}</b>
            </div>
            <div className="gp-level">
              إجمالي النقاط
              <b className="gp-num">{fmt(p?.summary?.total_score)}</b>
            </div>
            <div className="gp-level">
              آخر نشاط
              <b>{formatRelative(p?.summary?.last_activity)}</b>
            </div>
          </div>

          <p className="gp-muted" style={{ marginTop: 10 }}>
            {p?.placement_test_taken
              ? `أجرى اختبار تحديد المستوى بتاريخ ${formatDate(p.placement_test_date)}`
              : "لم يُجرِ اختبار تحديد المستوى بعد"}
            {data.joined_at && `. انضم للمجموعة بتاريخ ${formatDate(data.joined_at)}`}
          </p>

          {p?.skills && (
            <>
              <h4 className="gp-sec-title">المستوى في كل مهارة</h4>
              <div className="gp-levels">
                {Object.entries(p.skills).map(([key, v]) => (
                  <div className="gp-level" key={key}>
                    {SKILL_LABELS[key] || key}
                    <b>{v.code === "not_tested" ? "لم يُختبر" : v.code}</b>
                  </div>
                ))}
              </div>
            </>
          )}

          <h4 className="gp-sec-title">التقدم في البرامج</h4>
          {programs.length === 0 ? (
            <EmptyState title="لم يبدأ أي برنامج" hint="لا يوجد نشاط لهذا الطالب في أي برنامج حتى الآن." />
          ) : (
            programs.map((pr) => (
              <ProgramCard
                key={pr.program}
                program={pr}
                open={Boolean(openPrograms[pr.program])}
                onToggle={() => setOpenPrograms((o) => ({ ...o, [pr.program]: !o[pr.program] }))}
              />
            ))
          )}
        </>
      )}
    </Drawer>
  );
}